/*
 * Measure a lawn on the satellite map while the client is on the phone: tap around the grass, the square feet add up.
 * Front and back yards are separate areas ("Add another area"), summed. Uses HQ's Google Maps key like the other maps.
 */
import { useEffect, useRef, useState } from "react";
import { useMapsKey } from "../api/hooks";
import { geocode, loadGoogleMaps, onMapsRefused } from "./googleMaps";
import { Banner, Button } from "./parts";
import { Modal } from "./overlays";

const SQFT_PER_M2 = 10.7639;
const HOME_BASE = { lat: 29.9511, lng: -90.0715 };
const GRASS = "#34C759";

export function LawnMeasure({
  open,
  address,
  onClose,
  onUse,
}: {
  open: boolean;
  /** The client's address, to start the map there. */
  address: string | null;
  onClose: () => void;
  onUse: (sqft: number) => void;
}) {
  return (
    <Modal title="Measure the lawn" open={open} onClose={onClose}>
      {open ? <MeasureMap address={address} onUse={onUse} /> : null}
    </Modal>
  );
}

function MeasureMap({ address, onUse }: { address: string | null; onUse: (sqft: number) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const shapes = useRef<google.maps.Polygon[]>([]);
  /** Starts a new area to tap around (set once the map is up). */
  const newArea = useRef<(() => void) | null>(null);
  const [areas, setAreas] = useState<number[]>([0]);
  const [problem, setProblem] = useState<string | null>(null);
  const mapsKey = useMapsKey();
  const key = mapsKey.data?.key ?? null;

  useEffect(() => {
    if (!key || !box.current) return;
    const el = box.current;
    let gone = false;
    const stop = onMapsRefused(() => setProblem("Google turned down the map key. Head office checks it in Settings."));
    loadGoogleMaps(key)
      .then(async () => {
        const { Map, Polygon } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;
        const { spherical } = (await google.maps.importLibrary("geometry")) as google.maps.GeometryLibrary;
        const at = address ? await geocode(address) : null;
        if (gone) return;
        const m = new Map(el, {
          center: at ?? HOME_BASE,
          zoom: at ? 20 : 11,
          mapTypeId: "satellite",
          tilt: 0,
          draggableCursor: "crosshair",
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          clickableIcons: false,
        });
        const fresh = () => {
          const p = new Polygon({
            map: m,
            editable: true,
            strokeColor: GRASS,
            fillColor: GRASS,
            fillOpacity: 0.3,
            clickable: false,
          });
          const measure = () => {
            const i = shapes.current.indexOf(p);
            const path = p.getPath();
            const sqft = path.getLength() >= 3 ? Math.round(spherical.computeArea(path) * SQFT_PER_M2) : 0;
            setAreas((a) => a.map((v, j) => (j === i ? sqft : v)));
          };
          for (const e of ["insert_at", "set_at", "remove_at"]) p.getPath().addListener(e, measure);
          shapes.current.push(p);
        };
        fresh();
        newArea.current = fresh;
        m.addListener("click", (e: google.maps.MapMouseEvent) => {
          if (e.latLng) shapes.current[shapes.current.length - 1]?.getPath().push(e.latLng);
        });
      })
      .catch(() => {
        if (!gone) setProblem("The map did not load. Check the internet and try again.");
      });
    return () => {
      gone = true;
      stop();
      for (const s of shapes.current) s.setMap(null);
      shapes.current = [];
      newArea.current = null;
    };
  }, [key, address]);

  const total = areas.reduce((s, a) => s + a, 0);
  const another = () => {
    if (!newArea.current || !areas[areas.length - 1]) return;
    newArea.current();
    setAreas((a) => [...a, 0]);
  };
  const undo = () => {
    const path = shapes.current[shapes.current.length - 1]?.getPath();
    if (path?.getLength()) path.pop();
  };
  const clear = () => {
    for (const s of shapes.current.slice(1)) s.setMap(null);
    shapes.current = shapes.current.slice(0, 1);
    shapes.current[0]?.getPath().clear();
    setAreas([0]);
  };

  if (mapsKey.data && !key)
    return <Banner>Add a Google Maps key at head office to measure on the map. Type the size instead.</Banner>;
  return (
    <div className="stack">
      {problem ? <Banner type="error">{problem}</Banner> : null}
      <p className="muted small">
        Tap the corners of the grass. Leave out the house, the driveway and flower beds. Drag a point to fix it.
      </p>
      <div ref={box} className="lawn-measure-map" />
      <div className="row lawn-measure-bar">
        <b className="num">{total.toLocaleString("en-US")} sq ft of grass</b>
        {areas.length > 1 ? <span className="muted small">{areas.length} areas</span> : null}
        <Button type="tertiary" onClick={undo}>
          Undo point
        </Button>
        <Button type="tertiary" onClick={another}>
          Add another area
        </Button>
        <Button type="tertiary" onClick={clear}>
          Start over
        </Button>
        <Button onClick={() => onUse(total)} disabled={!total}>
          Use this size
        </Button>
      </div>
    </div>
  );
}
